import React, { useEffect, useRef } from 'react';
import SEO from '../../components/SEO';
import { seoData } from '../../utils/seoData';

const VisionMissionPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const values = [
    {
      icon: "fas fa-users",
      title: "Consumer Obsessed",
      description: "We prioritize quality, quantity, and service excellence, ensuring every consumer interaction exceeds expectations through deep understanding of their needs."
    },
    {
      icon: "fas fa-lightbulb",
      title: "Innovation First",
      description: "We embrace technology to bridge gaps in the traditional metals industry, constantly seeking innovative solutions to complex supply chain challenges."
    },
    {
      icon: "fas fa-puzzle-piece",
      title: "Problem Solvers",
      description: "Our team of enthusiasts thrives on finding solutions to problems, no matter how complex, with curiosity and determination as our driving forces."
    },
    {
      icon: "fas fa-award",
      title: "Excellence Standard",
      description: "We maintain the highest standards in service and quality, setting new benchmarks for what consumers can expect from the metal industry."
    },
    {
      icon: "fas fa-link",
      title: "End-to-End Integration",
      description: "We understand that the metal business is a sum of many parts, and we develop comprehensive solutions from raw materials to final products."
    },
    {
      icon: "fas fa-microscope",
      title: "Deep Expertise",
      description: "We develop profound understanding of metals, especially aluminium and steel, to create informed, effective solutions for our industry."
    }
  ];

  const innovations = [
    {
      title: "Tech Integration",
      description: "Implementing cutting-edge technology to modernize traditional metal industry processes and enhance efficiency."
    },
    {
      title: "Supply Chain Optimization",
      description: "Creating seamless connections from raw materials to finished products through intelligent system integration."
    },
    {
      title: "Consumer-Centric Design",
      description: "Developing products and services with deep focus on consumer needs, quality expectations, and service excellence."
    },
    {
      title: "Material Expertise",
      description: "Building comprehensive knowledge of aluminium, steel, and other metals to deliver superior solutions and insights."
    }
  ];

  return (
    <div className="vision-mission-page">
      <SEO 
        title={seoData.visionMission.title}
        description={seoData.visionMission.description}
        keywords={seoData.visionMission.keywords}
        canonical={seoData.visionMission.canonical}
        ogImage={seoData.visionMission.ogImage}
      />
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .vision-mission-page {
          font-family: 'Poppins', sans-serif;
          line-height: 1.6;
          color: #333;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }
        
        .hero-bg-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.3;
          z-index: 1;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #9A60FF 0%, #7B3FE4 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 15px 40px rgba(123, 63, 228, 0.4);
          transition: all 0.3s ease;
        }
        
        .hero-icon:hover {
          transform: scale(1.1);
          box-shadow: 0 20px 50px rgba(90, 31, 179, 0.5);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        /* Vision Mission Grid */
        .vm-section {
          padding: 100px 0;
          background: #F6F7F9;
        }

        .vm-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          margin-bottom: 4rem;
        }

        .vm-card {
          background: white;
          padding: 3rem;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
          transform: translateY(0);
          transition: all 0.4s ease;
        }

        .vm-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .vm-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 5px;
          background: linear-gradient(90deg, #7B3FE4, #9A60FF);
        }
        
        .image-placeholder {
          width: 100%;
          height: 200px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px dashed #7B3FE4;
          transition: all 0.3s ease;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .image-placeholder:hover {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
          transform: scale(1.02);
        }

        .placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .placeholder-content i {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          display: block;
        }

        .placeholder-text {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .placeholder-desc {
          font-size: 0.9rem;
          opacity: 0.8;
          max-width: 250px;
        }

        .vm-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #7B3FE4, #9A60FF);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          transition: all 0.3s ease;
        }
        
        .vm-icon:hover {
          transform: rotate(5deg) scale(1.05);
        }

        .vm-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .vm-card h2 {
          font-size: 2rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .vm-card p {
          font-size: 1.1rem;
          color: #4A5260;
          line-height: 1.8;
        }

        /* Core Values Section */
        .values-section {
          padding: 100px 0;
          background: white;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        .values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .value-card {
          background: #F6F7F9;
          padding: 2.5rem;
          border-radius: 12px;
          text-align: center;
          position: relative;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .value-card:hover {
          border-color: #7B3FE4;
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(123, 63, 228, 0.2);
        }

        .value-icon {
          width: 70px;
          height: 70px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 5px 20px rgba(123, 63, 228, 0.2);
        }

        .value-icon i {
          font-size: 1.8rem;
          background: linear-gradient(135deg, #7B3FE4 0%, #9A60FF 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .value-card h3 {
          font-size: 1.4rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .value-card p {
          color: #4A5260;
          line-height: 1.6;
        }

        /* Innovation Section */
        .innovation-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          color: white;
          position: relative;
          overflow: hidden;
        }
        
        .innovation-image {
          width: 100%;
          max-width: 800px;
          height: 400px;
          object-fit: cover;
          border-radius: 15px;
          margin: 2rem auto;
          display: block;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .innovation-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="%23ffffff" opacity="0.03"><circle cx="20" cy="20" r="2"/><circle cx="40" cy="40" r="2"/><circle cx="60" cy="20" r="2"/><circle cx="80" cy="40" r="2"/><circle cx="20" cy="60" r="2"/><circle cx="40" cy="80" r="2"/><circle cx="60" cy="60" r="2"/><circle cx="80" cy="80" r="2"/></svg>');
          background-repeat: repeat;
        }

        .innovation-content {
          position: relative;
          z-index: 2;
          text-align: center;
        }

        .innovation-content h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 2rem;
        }

        .innovation-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .innovation-item {
          background: rgba(255, 255, 255, 0.1);
          padding: 2rem;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .innovation-item h4 {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #9A60FF 0%, #14B87A 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1E2329 0%, #5A1FB3 100%);
          text-align: center;
          color: white;
        }
        
        .cta-content h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }

        .cta-content p {
          font-size: 1.3rem;
          margin-bottom: 2.5rem;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-block;
          padding: 18px 40px;
          background: white;
          color: #7B3FE4;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.4s ease;
          cursor: pointer;
          border: none;
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.2);
        }

        .cta-button:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 15px 40px rgba(255, 255, 255, 0.3);
          background: #F6F7F9;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .vm-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .vm-card {
            padding: 2rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .innovation-content h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      {/* Add Font Awesome CSS */}
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-industry"></i>
          </div>
          <h1 className="animate-fade-in">Vision & Mission</h1>
          <p className="hero-subtitle animate-fade-in">
            Revolutionizing the metal supply chain through technology, innovation, and unwavering commitment to consumer excellence
          </p>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="vm-section">
        <div className="container">
          <div className="vm-grid">
            <div className="vm-card animate-on-scroll">

              <div className="vm-icon">
                <i className="fas fa-eye"></i>
              </div>
              <h2>Our Vision</h2>
              <p>
                To unlock the full value of the metal supply chain through tech-enabled, consumer-centric products. 
                We envision a future where the traditional metal business transforms into an integrated ecosystem—from 
                raw materials to final products—powered by technology and driven by deep understanding of aluminium, 
                steel, and consumer needs.
              </p>
            </div>
            
            <div className="vm-card animate-on-scroll">
              <div className="vm-icon">
                <i className="fas fa-bullseye"></i>
              </div>
              <h2>Our Mission</h2>
              <p>
                To build a curious and capable team of enthusiasts who relentlessly find solutions to problems, no matter 
                the challenge. We are committed to being the best in service and quality for all our consumers, bridging 
                the technology gap in the metals industry with innovative, consumer-obsessed approaches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Core Values</h2>
            <p>The principles that drive our commitment to excellence and innovation in the metal supply chain</p>
          </div>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card animate-on-scroll">
                <div className="value-icon">
                  <i className={value.icon}></i>
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Focus Section */}
      <section className="innovation-section">
        <div className="container">
          <div className="innovation-content">
            <h2>Transforming Metal Supply Chain</h2>
            <p style={{fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9}}>
              Bridging the technology gap in metals through innovative solutions
            </p>
            

            
            <div className="innovation-grid">
              {innovations.map((item, index) => (
                <div key={index} className="innovation-item animate-on-scroll">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Together?</h2>
            <p>
              Join us in revolutionizing the metal supply chain through innovation, technology, 
              and unwavering commitment to excellence.
            </p>
            <button className="cta-button" onClick={() => window.location.href = '/contact/enquiry-form'}>
              Get Started Today
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisionMissionPage;